import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Badge, Card, PageHeader, Spinner } from "@empite/ui-components";
import type { Operator, TenantRosterRow } from "../admin-client";
import { useSetTenantPaymentsHandledExternally, useTenant } from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";
import { intervalLabel } from "./tiers";

/** The grants this page reads and writes with. Hiding a control is a courtesy;
 *  the Admin API re-checks and audits the refusal. */
const READ = "tenants:read_all";
const MANAGE = "tenants:manage";

/**
 * One company: the roster row, and the one switch the platform keeps per
 * company about money — whether its own application takes its users' payments.
 *
 * When that switch is on, the platform refuses its own top-up checkout and
 * subscription routes for the company's applications, so no card or payment
 * record for those users can land here. The company's backend reports what was
 * paid through the tenant signal routes instead, and those keep working.
 */
export function TenantDetail({ tenantId }: { tenantId: string }) {
  return (
    <RequireOperator>
      {(session) =>
        can(session.operator, READ) ? (
          <TenantPage tenantId={tenantId} operator={session.operator} />
        ) : (
          <>
            <PageHeader title="Company" />
            <Card>
              <Alert tone="info" title="Companies cross the tenant boundary">
                <p>
                  Only platform staff can read another company. Your own company&rsquo;s tier is
                  on <strong>Billing</strong>.
                </p>
              </Alert>
            </Card>
          </>
        )
      }
    </RequireOperator>
  );
}

function TenantPage({ tenantId, operator }: { tenantId: string; operator: Operator }) {
  const detail = useTenant(tenantId);
  const [notice, setNotice] = useState<string | null>(null);

  if (detail.isPending) {
    return <Spinner label="Loading the company" />;
  }
  if (detail.error || !detail.data) {
    return <ErrorAlert error={detail.error} title="Could not load the company" />;
  }

  const tenant = detail.data.tenant;

  return (
    <>
      <PageHeader
        title={tenant.name}
        description={
          <>
            <code>{tenant.slug}</code> · <Link to="/tenants">back to all companies</Link>
          </>
        }
        actions={<StatusBadge status={tenant.status} />}
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <Card title="Company">
        <dl className="esm-detail-list">
          <Fact label="Company ID">
            <code>{tenant.id}</code>
          </Fact>
          <Fact label="Created">{when(tenant.created_at)}</Fact>
          <Fact label="Tier">
            {tenant.tier ? (
              <>
                {tenant.tier.name} — {tenant.tier.price.currency} {tenant.tier.price.amount}{" "}
                {intervalLabel(tenant.tier)}
              </>
            ) : (
              <Badge tone="neutral">Not metered</Badge>
            )}
          </Fact>
        </dl>
      </Card>
      <PaymentsCard tenant={tenant} operator={operator} onDone={setNotice} />
    </>
  );
}

function PaymentsCard({
  tenant,
  operator,
  onDone,
}: {
  tenant: TenantRosterRow;
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const setFlag = useSetTenantPaymentsHandledExternally();
  const on = tenant.payments_handled_externally;
  const next = !on;

  return (
    <Card title="Payments">
      <dl className="esm-detail-list">
        <Fact label="Payments handled by tenant application">
          <Badge tone={on ? "success" : "neutral"}>{on ? "Yes" : "No"}</Badge>
        </Fact>
      </dl>
      <p className="esm-muted-text">
        {on ? (
          <>
            This company&rsquo;s own application takes its users&rsquo; payments. The platform
            refuses its top-up checkout and subscription routes for this company&rsquo;s
            applications; the company reports paid credits and subscriptions through the tenant
            signal routes.
          </>
        ) : (
          <>
            The platform takes payments for this company&rsquo;s applications through its own
            top-up checkout and subscription routes.
          </>
        )}
      </p>
      <div className="esm-actions">
        {can(operator, MANAGE) ? (
          <ReasonButton
            variant={next ? "primary" : "danger"}
            busy={setFlag.isPending}
            mutation={{
              title: next
                ? `Hand payments to ${tenant.name}'s application`
                : `Take payments back for ${tenant.name}`,
              description: <p>{describe(next)}</p>,
              confirmLabel: next ? "Turn on" : "Turn off",
              tone: next ? "primary" : "danger",
              run: async (reason) => {
                const result = await setFlag.mutateAsync({
                  tenantID: tenant.id,
                  enabled: next,
                  reason,
                });
                onDone(
                  `Payments handled by tenant application is now ${
                    result.tenant.payments_handled_externally ? "on" : "off"
                  } for ${result.tenant.name}.`,
                );
              },
            }}
          >
            {next ? "Turn on" : "Turn off"}
          </ReasonButton>
        ) : (
          <span className="esm-muted-text">
            Your roles do not grant <code>{MANAGE}</code>.
          </span>
        )}
      </div>
    </Card>
  );
}

function describe(enabled: boolean): string {
  return enabled
    ? "From now on the platform refuses top-up checkouts and subscription starts and cancellations for this company's applications. Subscriptions already live on the platform are not cancelled, but can no longer be changed here."
    : "The platform's own top-up checkout and subscription routes start accepting this company's users again. Make sure the company's application has stopped charging them, or a user could pay twice.";
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
